const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
const dtf = new Intl.DateTimeFormat('es', { dateStyle: 'medium', timeStyle: 'short' });

const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 30],
  ['month', 12],
  ['year', Infinity],
];

export function relativeTime(iso: string | null, now = Date.now()): string {
  if (!iso) return '—';
  let delta = (Date.parse(iso) - now) / 1000;
  for (const [unit, size] of STEPS) {
    if (Math.abs(delta) < size) return rtf.format(Math.round(delta), unit);
    delta /= size;
  }
  return dtf.format(new Date(iso));
}

export function dateTime(iso: string | null): string {
  return iso ? dtf.format(new Date(iso)) : '—';
}

export function duration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

export function shortId(id: string): string {
  return id.slice(0, 8);
}

export function truncate(text: string, max = 80): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const STOP_WORDS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'y', 'e']);

/** Two-letter monogram for avatars: "Guía de onboarding" → "GO", "ana@acme.co" → "AA". */
export function initials(name: string): string {
  const words = name.split(/[\s.@_-]+/).filter((w) => w && !STOP_WORDS.has(w.toLowerCase()));
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Brand palette an agent avatar can take; module colours double as identity here, never as chrome. */
export const AVATAR_TONES = ['cyan', 'violet', 'indigo', 'emerald', 'amber', 'blue'] as const;

/** Stable tone per seed (use the agent id so renaming keeps the colour). */
export function avatarTone(seed: string): (typeof AVATAR_TONES)[number] {
  let h = 7;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length]!;
}

/** Sizes in the Spanish number format used across the app: "850 B", "2,4 MB", "1 GB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = value < 10 && !Number.isInteger(Math.round(value * 10) / 10) ? 1 : 0;
  return `${value.toLocaleString('es-CO', { maximumFractionDigits: digits })} ${units[unit]}`;
}
